// #region imports

// --- Angular
import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

// --- RxJs
import { Observable, of, Subscription } from 'rxjs';
import { catchError, subscribeOn, map, tap, shareReplay } from 'rxjs/operators';

// --- 3rd Party
import { retryBackoff } from 'backoff-rxjs';
import { TranslateService } from '@ngx-translate/core';

// --- HS Code
import { environment } from '../../../../environments/environment';
import {
  ApplicationError,
  Constants,
  ContextQuestionModel,
  ErrorMessage,
  PatchModel,
  PatchOperations,
  QuestionResponseModel,
  SurveyResponseContexts,
  SurveyResponseModel,
  SurveyStatuses,
} from '../../../models';
import { AppService } from '../app.service';
import { QuestionsBaseService } from '..';
import { ResponseBaseService } from './response-base.service';

// #endregion

@Injectable({
  providedIn: 'root'
})
export class ResponseService extends ResponseBaseService implements OnDestroy {
  private subs: Subscription = Subscription.EMPTY;
  private _isLoading: boolean;
  private _surveyIdentifier: string;
  private _currentQuestionUpdating: ContextQuestionModel[];

  constructor(
    private http: HttpClient,
    private appService: AppService,
    private questionsService: QuestionsBaseService,
    private translate: TranslateService
  ) {
    super(questionsService);

    this._isLoading = false;
    this._surveyIdentifier = '';

    this._currentQuestionUpdating = new Array<ContextQuestionModel>();
  }

  override ngOnDestroy() {
    if (this.subs != null) {
      this.subs.unsubscribe();
      this.subs = Subscription.EMPTY;
    }
  }

  loadResponse(surveyIdentifier: string): void {
    if (this._isLoading) {
      return;
    }

    if (surveyIdentifier && (surveyIdentifier === this._surveyIdentifier)) {
      this.updateProgress();
      return;
    }

    if (
      !surveyIdentifier && this._activeResponse$.value
    ) {
      const surveyResponse = Object.assign(
        {} as SurveyResponseModel,
        this._activeResponse$.value
      );
      const questionResponses = this._activeResponse$.value && [
        ...this._activeResponse$.value.questionResponses
      ];
      surveyResponse.questionResponses = questionResponses as QuestionResponseModel[];
      this._activeResponse$.next(surveyResponse);
      this.initialiseQuestionResponses(surveyResponse.surveyId, surveyResponse.questionResponses, surveyResponse.responseContext);
      return;
    } else if (!surveyIdentifier) {
      return;
    }

    this._isLoading = true;
    this._surveyIdentifier = surveyIdentifier;

    this.appService.setLoading(true);

    const url =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.responseApi +
      `/${surveyIdentifier}`;

    this.http
      .get<SurveyResponseModel | null>(url)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._modelError.bind(this))
      )
      .subscribe((surveyResponse: SurveyResponseModel | null) => {
        if (surveyResponse) {
          this.questionsService.clearQuestions();
          this._isLoading = false;
          if (
            surveyResponse.responseContext === SurveyResponseContexts.respondent &&
            surveyResponse.status <= SurveyStatuses.respondentInvitations
          ) {
            surveyResponse.status = SurveyStatuses.introduction;
          }
          surveyResponse.numberOfRespondentsPerGroup = surveyResponse.numberOfOthers / surveyResponse.numberOfGroups;
          this._activeResponse$.next(surveyResponse);
          this.initialiseQuestionResponses(surveyResponse.surveyId, surveyResponse.questionResponses, surveyResponse.responseContext);
          this.updateProgress();
        }
        this.appService.setLoading(false);
      });
  }

  updateStatus(surveyResponseModel: SurveyResponseModel): Observable<SurveyResponseModel | null> {
    if (!surveyResponseModel) {
      return of(null);
    }

    const patchModel = {
      operation: PatchOperations.replace,
      path: 'status',
      value: surveyResponseModel.status.toString()
    } as PatchModel;
    const url =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.responseApi +
      `/${surveyResponseModel.id}`;

    this.appService.setLoading(true);
    this._isUpdating$.next(true);

    const request$ = this.http
      .patch<SurveyResponseModel>(url, patchModel)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._modelError.bind(this)),
        tap(response => {
          this.appService.setLoading(false);

          if (response) {
            if (this._activeResponse$.value) {
              response.questionResponses =
              this._activeResponse$.value.questionResponses as QuestionResponseModel[];
            }
            response.numberOfRespondentsPerGroup = response.numberOfOthers / response.numberOfGroups;

            this._activeResponse$.next(response);
            this._surveyIdentifier = '';
          }

          this._isUpdating$.next(false);
        }),
        shareReplay(1)
      );

    request$.subscribe();

    return request$;
  }

  updateComment(name: string, value: string): void {
    if (!value) {
      return;
    }

    const activeResponse = this._activeResponse$.value;
    if (!activeResponse) {
      return;
    }

    const url = environment.apiConfig.surveyApiUrl + environment.apiConfig.responseApi + `/${activeResponse.id}`;
    const model = {
      operation: PatchOperations.replace,
      path: name,
      value
    } as PatchModel;
    this.http.patch<SurveyResponseModel | null>(url, model).pipe(
      retryBackoff(environment.retryConfig),
      catchError(this._responsePatchError.bind(this))
    ).subscribe((response: SurveyResponseModel | null) => {
      if (!response) return;
      this._activeResponse$.next(response);
    });
  }

  questionAnswered(response: ContextQuestionModel): void {
    if (!response) {
      return;
    }

    this._isUpdating$.next(true);

    this._currentQuestionUpdating.push(response);

    const url =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.questionResponseApi +
      `/${response.questionResponseId}`;
    this.http
      .put(url, response)
      .pipe(
        map(() => true),
        retryBackoff(environment.retryConfig),
        catchError(this._questionModelError.bind(this))
      )
      .subscribe((success: boolean) => {
        const updated: QuestionResponseModel | null | undefined =
          this._activeResponse$.value &&
          this._activeResponse$.value.questionResponses.find(
            qr => qr.id === response.questionResponseId
          );
        if (updated) {
          if (!success) {
            updated.response = null;
            const newResponse = structuredClone(this._activeResponse$.value)
            this._activeResponse$.next(newResponse);
            this.initialiseQuestionResponses(
              newResponse!.surveyId, newResponse!.questionResponses, newResponse!.responseContext);
            return;
          }
          updated.response = response.response;

          let i = this._currentQuestionUpdating.findIndex(o => {
              return o.questionResponseId.toString() === response.questionResponseId.toString()
          })

          if (i != -1) {
            if (this._currentQuestionUpdating.length === 1) {
              this._currentQuestionUpdating = new Array<ContextQuestionModel>();
              this._isUpdating$.next(false);
            }
            else {
              this._currentQuestionUpdating.splice(i, 1);
              this._isUpdating$.next(true);
            }
          }

        }
        this.updateProgress();
      });
  }

  update(
    model: import('../../../models').SurveyResponseModel
  ): import('rxjs').Observable<boolean> {
    throw new Error('Method not implemented.');
  }

  onNext(response: SurveyResponseModel, isEffectivenessNext: boolean = false): void {
    const activeResponse = this._activeResponse$.value;
    if (activeResponse) {
      this.initialiseQuestionResponses(activeResponse.surveyId, activeResponse.questionResponses, activeResponse.responseContext);

      if (this._groupIndex$.value === 5) {
        if (isEffectivenessNext) {
          if (response.responseContext === SurveyResponseContexts.respondent) {
            response.status = SurveyStatuses.commentsQuestions;
          } else {
            response.status = SurveyStatuses.extraValueQuestions;
          }
          this.updateStatus(response);
        } else {
          if (response.responseContext === SurveyResponseContexts.respondent){
            response.status = SurveyStatuses.commentsQuestions;
          }else{
          response.status = SurveyStatuses.effectivenessQuestions;
          }
          this.updateStatus(response);
          }
      } else if (this._groupIndex$.value === 6) {
        if (response.responseContext === SurveyResponseContexts.respondent) {
          response.status = SurveyStatuses.commentsQuestions;
        } else {
          response.status = SurveyStatuses.extraValueQuestions;
        }
        this.updateStatus(response);
      }
    }
  }

  private _responsePatchError(err: any, caught: Observable<SurveyResponseModel | null>): Observable<SurveyResponseModel | null> {
    this._handleError(err);
    this._isLoading = false;
    return of(null);
  }

  private _modelError(err: any): Observable<SurveyResponseModel | null> {
    this._handleError(err);
    this._isLoading = false;
    return of(null);
  }

  private _questionModelError(err: any, response: Observable<boolean>): Observable<boolean> {
    let detailedMessage: string | undefined;
    let statusCode: number = -1;

    if (err && err instanceof HttpErrorResponse) {
      detailedMessage = `${err.statusText}<br/>${err.message}`;
      statusCode = err.status;
    }

    const error = ApplicationError.create(
      ErrorMessage.surveyQuestionSaveErrorCode,
      'responseService',
      false,
      err,
      detailedMessage,
      undefined,
      statusCode
    );
    error.errorHeader = 'v2ErrorHeader_SaveQuestionError'
    this.appService.setLastError(error);

    this._isLoading = false;
    return of(false);
  }

  private _handleError(err: any) {

    this.appService.setLoading(false);

    let error: ApplicationError;
    if (err && err instanceof HttpErrorResponse) {
      if (err.status === Constants.HTTP_CONFLICT) {
        this._activeResponse$.next(null);
        this.loadResponse(this._surveyIdentifier);
        const activeResponse = this._activeResponse$.value;
        this._activeResponse$.next(activeResponse);
        return;
      }

      // A client-side or network error occurred. Handle it accordingly.
      let detailedMessage = `${err.message}`;
      if (err.statusText) {
        detailedMessage += `<br/>${err.statusText}`;
      }
      error = ApplicationError.create(
        ErrorMessage.genericErrorCode,
        'ResponseService',
        true,
        undefined,
        detailedMessage
      );
    } else{
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      error = ApplicationError.create(
        ErrorMessage.genericErrorCode,
        'ResponseService',
        true,
        err
      );
    }

    this.appService.setLastError(error);
  }
}
